/**
 * JWT Token Management Utilities
 * Handles storage, retrieval, validation, and extraction of JWT tokens
 */

const JWT_TOKEN_KEY = 'skilladvent_jwt_token';

/**
 * Store JWT token in localStorage
 * @param {string} token - The JWT token to store
 */
export const storeToken = (token) => {
  try {
    localStorage.setItem(JWT_TOKEN_KEY, token);
    console.log('JWT token stored successfully');
  } catch (error) {
    console.error('Failed to store JWT token:', error);
    throw new Error('Failed to store authentication token');
  }
};

/**
 * Retrieve JWT token from localStorage
 * @returns {string|null} The stored JWT token or null if not found
 */
export const getToken = () => {
  try {
    return localStorage.getItem(JWT_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve JWT token:', error);
    return null;
  }
};

/**
 * Remove JWT token from localStorage
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(JWT_TOKEN_KEY);
    console.log('JWT token removed successfully');
  } catch (error) {
    console.error('Failed to remove JWT token:', error);
  }
};

/**
 * Check if a JWT token exists and is not expired
 * @returns {boolean} True if token exists and is valid
 */
export const isTokenValid = () => {
  const token = getToken();
  if (!token) {
    return false;
  }

  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired
    if (payload.exp && payload.exp < currentTime) {
      console.log('JWT token has expired');
      removeToken();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Failed to validate JWT token:', error);
    removeToken();
    return false;
  }
};

/**
 * Extract JWT token from URL parameters
 * @param {string} url - The URL to extract token from (defaults to current URL)
 * @returns {string|null} The JWT token if found in URL, null otherwise
 */
export const extractTokenFromUrl = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const token = urlObj.searchParams.get('token');
    
    if (token) {
      console.log('JWT token found in URL');
      return token;
    }
    
    return null;
  } catch (error) {
    console.error('Failed to extract token from URL:', error);
    return null;
  }
};

/**
 * Get token payload (decoded JWT)
 * @returns {object|null} The decoded JWT payload or null if invalid
 */
export const getTokenPayload = () => {
  const token = getToken();
  if (!token) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = () => {
  const payload = getTokenPayload();
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * Get time until token expires (in seconds)
 * @returns {number} Seconds until expiration, or 0 if expired/invalid
 */
export const getTokenTimeToExpiry = () => {
  const payload = getTokenPayload();
  if (!payload || !payload.exp) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeToExpiry = payload.exp - currentTime;
  return Math.max(0, timeToExpiry);
};
